import type { UpdateUserDto, UserDto } from "@stylingcv/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export const updateUser = async (data: UpdateUserDto) => {
  const response = await axios.patch<UserDto, AxiosResponse<UserDto>, UpdateUserDto>(
    "/user/me",
    data,
  );

  return response.data;
};

export const useUpdateUser = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateUserFn,
  } = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.setQueryData(["user"], data);
    },
  });

  // Enhanced updateUser function that returns parsed errors
  const updateUserWithErrorParsing = async (
    data: UpdateUserDto,
  ): Promise<{ success: boolean; fieldErrors?: Record<string, string> }> => {
    try {
      await updateUserFn(data);
      return { success: true };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";

      // Parse specific field errors
      const fieldErrors: Record<string, string> = {};

      if (errorMessage.includes("email address already exists")) {
        fieldErrors.email = "This email address is already in use. Please use a different email.";
      }

      if (errorMessage.includes("username is already taken")) {
        fieldErrors.username =
          "This username is already taken. Please choose a different username.";
      }

      return { success: false, fieldErrors } as any;
    }
  };

  return { updateUser: updateUserFn, updateUserWithErrorParsing, loading, error };
};
